import { useState, type FC } from "react";

import type { ExecutionFlow } from "../../types";

import { useToast } from "../../context/ToastContext";
import { startACompileRecordingForACompletedExecutionFlowAsUser } from "../../dashboard-client";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";

interface CompileRecordingActionCardProps {
  compileRecordingEnabled: boolean;
  flow: ExecutionFlow;
  compileRecordingRequestIds: string[];
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Failed to start compile recording.";
}

export const CompileRecordingActionCard: FC<
  CompileRecordingActionCardProps
> = ({ compileRecordingEnabled, flow, compileRecordingRequestIds }) => {
  const [isStarting, setIsStarting] = useState(false);
  const [startedIds, setStartedIds] = useState<string[]>([]);
  const toast = useToast();

  if (!compileRecordingEnabled || flow.status !== "succeeded") {
    return null;
  }

  const allIds = [...startedIds, ...compileRecordingRequestIds];
  const hasRecordings = allIds.length > 0;

  const handleStart = async () => {
    setIsStarting(true);
    try {
      const { data } =
        await startACompileRecordingForACompletedExecutionFlowAsUser({
          path: { flowId: flow.id },
        });
      if (data.status === "started" && data.request_id) {
        setStartedIds((prev) => [data.request_id!, ...prev]);
        toast.present({
          body: `Compile recording ${data.request_id.slice(0, 8)} has been queued. It will appear as a new flow.`,
          title: "Recording started",
          tone: "success",
        });
        return;
      }
      toast.present({
        body: "This execution flow is not eligible for compile recording.",
        title: "Cannot start recording",
        tone: "info",
      });
    } catch (error) {
      toast.present({
        body: toErrorMessage(error),
        title: "Recording failed",
        tone: "error",
      });
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Alert
      variant="info"
      title="Compile & Record"
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          loading={isStarting}
          loadingText="Starting..."
          onClick={() => void handleStart()}
          className="border-orange-400/50 text-orange-700 hover:bg-orange-100 dark:border-orange-500/60 dark:text-orange-100 dark:hover:bg-orange-500/20"
        >
          <svg
            className="mr-1.5 h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
            />
          </svg>
          {hasRecordings ? "New Recording" : "Start Recording"}
        </Button>
      }
    >
      {hasRecordings ? (
        <div className="space-y-2">
          <span>
            {allIds.length === 1
              ? "A compile recording exists for this execution."
              : `${allIds.length} compile recordings exist for this execution.`}{" "}
            You can start a new one.
          </span>
          <div className="flex flex-wrap gap-1.5">
            {allIds.map((id, index) => (
              <Badge
                key={id}
                variant={index === 0 ? "info" : "default"}
                size="sm"
              >
                {id.slice(0, 8)}
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        "Compile the workspace, bring localhost online, and record a QA session for this execution."
      )}
    </Alert>
  );
};
